/**
 * Recruitment System React Hooks
 *
 * Drop-in replacement for localStorage-based recruitment hooks.
 * Integrates with Supabase database via service layer.
 * Handles automatic camelCase ↔ snake_case conversion.
 *
 * Usage:
 * ```typescript
 * const {
 *   recruits,
 *   loading,
 *   error,
 *   createRecruit,
 *   updateRecruit,
 *   deleteRecruit,
 *   template,
 *   saveTemplate,
 *   getInstance,
 *   saveInstance
 * } = useRecruitment();
 * ```
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/useAuth';
import { createComponentLogger } from '@/utils/logger';
import { supabase } from '@/utils/supabase';

const log = createComponentLogger('useRecruitment', 'recruitment-hooks');
import {
  getRecruits,
  getRecruit,
  createRecruit as createRecruitDb,
  updateRecruit as updateRecruitDb,
  deleteRecruit as deleteRecruitDb,
  getActiveContractTemplate,
  createContractTemplate,
  updateContractTemplate,
  getContractInstance,
  createContractInstance,
  updateContractInstance,
  updateCheckboxStates as updateCheckboxStatesDb,
  getLatestTemplateVersion,
  regenerateContractFromTemplate
} from '@/services/recruitmentService';
import {
  recruitFromDatabase,
  recruitsFromDatabase,
  recruitWithRelationsFromDatabase,
  recruitToDatabase,
  recruitUpdateToDatabase,
  contractInstanceFromDatabase,
  contractInstanceToDatabase,
  type RecruitmentCandidate,
  type RecruitmentCandidateWithRelations,
  type ContractInstanceData,
  type ContractTemplate
} from '@/types/recruitment';
import type { RecruitmentFormValues } from '@/components/recruitment/validations';
import type { ContractSection, ContractVariableData } from '@/components/recruitment/contract-types';
import { createDefaultContractTemplate } from '@/components/recruitment/contract-seed-template';

// ============================================================================
// Hook: useRecruits - Manage all recruits for organization
// ============================================================================

export function useRecruits() {
  const { user } = useAuth();
  const [recruits, setRecruits] = useState<RecruitmentCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all recruits
  const fetchRecruits = useCallback(async () => {
    if (!user?.organization_id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const dbRecruits = await getRecruits(user.organization_id);
      const frontendRecruits = recruitsFromDatabase(dbRecruits);
      setRecruits(frontendRecruits);
    } catch (err) {
      log.error('Error fetching recruits', { organizationId: user.organization_id }, err as Error);
      setError(err instanceof Error ? err.message : 'Failed to fetch recruits');
    } finally {
      setLoading(false);
    }
  }, [user?.organization_id]);

  // Load on mount
  useEffect(() => {
    fetchRecruits();
  }, [fetchRecruits]);

  return {
    recruits,
    loading,
    error,
    refetch: fetchRecruits
  };
}

// ============================================================================
// Hook: useRecruit - Manage single recruit with relations
// ============================================================================

export function useRecruit(recruitId?: string) {
  const [recruit, setRecruit] = useState<RecruitmentCandidateWithRelations | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch single recruit with relations
  const fetchRecruit = useCallback(async () => {
    if (!recruitId) {
      setLoading(false);
      setRecruit(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const dbRecruit = await getRecruit(recruitId);
      if (dbRecruit) {
        const frontendRecruit = recruitWithRelationsFromDatabase(dbRecruit);
        setRecruit(frontendRecruit);
      } else {
        setRecruit(null);
      }
    } catch (err) {
      log.error('Error fetching recruit', { recruitId }, err as Error);
      setError(err instanceof Error ? err.message : 'Failed to fetch recruit');
      setRecruit(null);
    } finally {
      setLoading(false);
    }
  }, [recruitId]);

  // Load on mount and when ID changes
  useEffect(() => {
    fetchRecruit();
  }, [fetchRecruit]);

  return {
    recruit,
    loading,
    error,
    refetch: fetchRecruit
  };
}

// ============================================================================
// Hook: useRecruitmentTemplate - Manage contract templates
// ============================================================================

export function useRecruitmentTemplate() {
  const { user } = useAuth();
  const [template, setTemplate] = useState<ContractTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch active template
  const fetchTemplate = useCallback(async () => {
    if (!user?.organization_id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const dbTemplate = await getActiveContractTemplate(user.organization_id);
      const codeTemplate = createDefaultContractTemplate();

      // Auto-create template if it doesn't exist
      if (!dbTemplate && user?.id) {
        log.info('No template found, creating default template', { organizationId: user.organization_id });

        try {
          await createContractTemplate(
            {
              organization_id: user.organization_id,
              name: codeTemplate.name,
              version: codeTemplate.version,
              description: codeTemplate.description || null,
              is_active: true,
              sections: codeTemplate.sections,
              required_variables: codeTemplate.requiredVariables,
              content_library: codeTemplate.contentLibrary || null
            },
            user.id
          );

          // Re-fetch the newly created template
          const newTemplate = await getActiveContractTemplate(user.organization_id);
          setTemplate(newTemplate);
          log.info('Default template created successfully', { organizationId: user.organization_id });
        } catch (createErr) {
          // Handle race condition: Another user created template simultaneously
          // PostgreSQL unique constraint violation error code is 23505
          const isUniqueViolation =
            createErr instanceof Error &&
            (createErr.message.includes('23505') ||
              createErr.message.includes('duplicate key') ||
              createErr.message.includes('idx_contract_templates_active_org'));

          if (isUniqueViolation) {
            log.warn('Template race condition detected - another user created template', {
              organizationId: user.organization_id
            });
            // Retry fetch - template should now exist
            const retryTemplate = await getActiveContractTemplate(user.organization_id);
            if (retryTemplate) {
              setTemplate(retryTemplate);
              log.info('Template fetched successfully after race condition', {
                organizationId: user.organization_id
              });
            } else {
              throw new Error('Template creation race condition - retry failed');
            }
          } else {
            // Re-throw non-race-condition errors
            throw createErr;
          }
        }
      } else if (dbTemplate && codeTemplate.version > dbTemplate.version && user?.id) {
        // Auto-upgrade: Code template has higher version than database template
        log.info('Template version upgrade detected', {
          organizationId: user.organization_id,
          fromVersion: dbTemplate.version,
          toVersion: codeTemplate.version
        });

        try {
          await updateContractTemplate(
            dbTemplate.id,
            {
              version: codeTemplate.version,
              sections: codeTemplate.sections,
              required_variables: codeTemplate.requiredVariables
            },
            user.id
          );

          // Re-fetch the updated template
          const updatedTemplate = await getActiveContractTemplate(user.organization_id);
          setTemplate(updatedTemplate);
          log.info('Template auto-upgraded successfully', {
            organizationId: user.organization_id,
            fromVersion: dbTemplate.version,
            toVersion: codeTemplate.version
          });
        } catch (updateErr) {
          // If upgrade fails, still use the existing template
          log.error('Template auto-upgrade failed, using existing template', {
            organizationId: user.organization_id,
            fromVersion: dbTemplate.version,
            toVersion: codeTemplate.version
          }, updateErr as Error);
          setTemplate(dbTemplate);
        }
      } else {
        setTemplate(dbTemplate);
      }
    } catch (err) {
      log.error('Error fetching/creating template', { organizationId: user?.organization_id }, err as Error);
      setError(err instanceof Error ? err.message : 'Failed to fetch template');
    } finally {
      setLoading(false);
    }
  }, [user?.organization_id, user?.id]);

  // Load on mount
  useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  // Save template (create or update)
  const saveTemplate = useCallback(
    async (templateData: ContractTemplate): Promise<boolean> => {
      if (!user?.organization_id || !user?.id) {
return false;
}

      try {
        setError(null);
        if (template?.id) {
          // Update existing
          await updateContractTemplate(template.id, templateData, user.id);
        } else {
          // Create new
          await createContractTemplate(
            {
              organization_id: user.organization_id,
              name: templateData.name,
              version: templateData.version,
              description: templateData.description || null,
              is_active: true,
              sections: templateData.sections,
              required_variables: templateData.requiredVariables,
              content_library: templateData.contentLibrary || null
            },
            user.id
          );
        }
        await fetchTemplate(); // Reload
        return true;
      } catch (err) {
        log.error('Error saving template', { organizationId: user?.organization_id }, err as Error);
        setError(err instanceof Error ? err.message : 'Failed to save template');
        return false;
      }
    },
    [user, template?.id, fetchTemplate]
  );

  return {
    template,
    loading,
    error,
    saveTemplate,
    refetch: fetchTemplate
  };
}

// ============================================================================
// Hook: useContractInstance - Manage contract instance for a recruit
// ============================================================================

export function useContractInstance(recruitId?: string) {
  const { user } = useAuth();
  const [instance, setInstance] = useState<ContractInstanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const subscriptionRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // Fetch instance with auto-regeneration for unsigned contracts
  const fetchInstance = useCallback(async () => {
    if (!recruitId) {
      setLoading(false);
      setInstance(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const dbInstance = await getContractInstance(recruitId);

      if (dbInstance) {
        // Check if unsigned contract needs template regeneration
        // Regenerate any contract where LO hasn't signed yet (draft, sent, viewed, accepted)
        // but NOT voided or already signed contracts
        const isUnsigned = !dbInstance.has_lo_signature && dbInstance.status !== 'voided';

        if (isUnsigned) {
          const latestVersion = await getLatestTemplateVersion();

          if (dbInstance.template_version < latestVersion) {
            log.info('Template version mismatch - regenerating unsigned contract', {
              recruitId,
              currentVersion: dbInstance.template_version,
              latestVersion
            });

            // Auto-regenerate from latest template
            const regenerated = await regenerateContractFromTemplate(dbInstance.id);

            if (regenerated) {
              const frontendInstance = contractInstanceFromDatabase(regenerated);
              setInstance(frontendInstance);
              return;
            }
          }
        }

        // Use existing instance (signed or already current)
        const frontendInstance = contractInstanceFromDatabase(dbInstance);
        setInstance(frontendInstance);
      } else {
        setInstance(null);
      }
    } catch (err) {
      log.error('Error fetching contract instance', { recruitId }, err as Error);
      setError(err instanceof Error ? err.message : 'Failed to fetch contract instance');
      setInstance(null);
    } finally {
      setLoading(false);
    }
  }, [recruitId]);

  // Handle real-time contract updates
  const handleContractUpdate = useCallback(
    async (payload: { eventType: string; new?: unknown; old?: unknown }) => {
      const { eventType, new: newRecord } = payload;

      log.info('Real-time contract update received', {
        eventType,
        recruitId,
        timestamp: new Date().toISOString()
      });

      if (eventType === 'UPDATE' && newRecord) {
        try {
          // Convert database record to frontend format
          const frontendInstance = contractInstanceFromDatabase(newRecord);
          setInstance(frontendInstance);
          // Silently update - no toast needed for background refreshes
        } catch (err) {
          log.error('Error processing real-time update', { recruitId }, err as Error);
        }
      }
    },
    [recruitId]
  );

  // Setup real-time subscription
  useEffect(() => {
    if (!recruitId || !user) {
      return;
    }

    // Clean up previous subscription
    if (subscriptionRef.current) {
      supabase.removeChannel(subscriptionRef.current);
      subscriptionRef.current = null;
    }

    // Subscribe to contract_instances changes for this recruit
    subscriptionRef.current = supabase
      .channel(`contract-instance-${recruitId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'contract_instances',
          filter: `recruit_id=eq.${recruitId}`
        },
        handleContractUpdate
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'contract_instances',
          filter: `recruit_id=eq.${recruitId}`
        },
        async (_payload) => {
          // When a new contract is inserted (e.g., after voiding), refetch to get active one
          log.info('New contract version detected, refetching', { recruitId });
          await fetchInstance();
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          log.info('Real-time contract subscription active', { recruitId });
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          log.warn('Real-time subscription failed', { recruitId, status });
        }
      });

    // Cleanup on unmount or recruitId change
    return () => {
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
      }
    };
  }, [recruitId, user, handleContractUpdate, fetchInstance]);

  // Load on mount
  useEffect(() => {
    fetchInstance();
  }, [fetchInstance]);

  // Save instance (create or update)
  const saveInstance = useCallback(
    async (
      variableData: ContractVariableData,
      templateId: string,
      templateVersion: number,
      templateSections: ContractSection[]
    ): Promise<boolean> => {
      if (!recruitId || !user?.organization_id || !user?.id) {
return false;
}

      // Validate sections exist
      if (!Array.isArray(templateSections) || templateSections.length === 0) {
        setError('Template must have sections to create contract instance');
        return false;
      }

      try {
        setError(null);
        if (instance?.id) {
          // Update existing
          await updateContractInstance(
            instance.id,
            {
              variable_data: variableData,
              sections: templateSections
            },
            user.id
          );
        } else {
          // Create new
          await createContractInstance(
            contractInstanceToDatabase(
              {
                templateId,
                templateVersion,
                variableData,
                sections: templateSections,
                status: 'draft'
              },
              recruitId,
              user.organization_id,
              user.id
            ),
            user.id
          );
        }
        await fetchInstance(); // Reload
        return true;
      } catch (err) {
        log.error('Error saving contract instance', { recruitId }, err as Error);
        setError(err instanceof Error ? err.message : 'Failed to save instance');
        return false;
      }
    },
    [recruitId, user, instance?.id, fetchInstance]
  );

  // Update checkbox states
  const updateCheckboxStates = useCallback(
    async (checkboxStates: Record<string, boolean>): Promise<boolean> => {
      if (!recruitId || !user?.id || !instance?.id) {
return false;
}

      try {
        setError(null);
        await updateCheckboxStatesDb(instance.id, checkboxStates, user.id);
        await fetchInstance(); // Reload
        return true;
      } catch (err) {
        log.error('Error updating checkbox states', { recruitId, instanceId: instance?.id }, err as Error);
        setError(err instanceof Error ? err.message : 'Failed to update checkboxes');
        return false;
      }
    },
    [recruitId, user?.id, instance?.id, fetchInstance]
  );

  return {
    instance,
    loading,
    error,
    saveInstance,
    updateCheckboxStates,
    refetch: fetchInstance
  };
}

// ============================================================================
// Hook: useRecruitment - Combined hook (matches old API)
// ============================================================================

/**
 * Combined hook that provides all recruitment functionality
 * Drop-in replacement for old useContract hook
 */
export function useRecruitment(recruitId?: string) {
  const { user } = useAuth();
  const recruitsHook = useRecruits();
  const templateHook = useRecruitmentTemplate();
  const instanceHook = useContractInstance(recruitId);

  // Create recruit
  const createRecruit = useCallback(
    async (formData: RecruitmentFormValues): Promise<RecruitmentCandidate | null> => {
      // Debug authentication state
      log.debug('Authentication state check', {
        hasUser: !!user,
        userId: user?.id,
        organizationId: user?.organization_id,
        userEmail: user?.email
      });

      if (!user?.organization_id || !user?.id) {
        const errorMsg = !user
          ? 'User not authenticated'
          : !user.id
            ? 'User ID missing'
            : 'Organization ID missing';
        log.error('Authentication check failed', { error: errorMsg });
        throw new Error(errorMsg);
      }

      try {
        log.debug('Converting form data to database format');
        const dbInsert = recruitToDatabase(formData, user.organization_id, user.id);

        log.debug('Calling database service to create recruit');
        const dbRecruit = await createRecruitDb(dbInsert, user.id);

        log.debug('Converting database result to frontend format');
        const frontendRecruit = recruitFromDatabase(dbRecruit);

        log.info('Recruit created successfully', { recruitId: frontendRecruit.id, email: formData.email });

        // Refresh list
        await recruitsHook.refetch();

        return frontendRecruit;
      } catch (err) {
        log.error('Error creating recruit', { email: formData.email }, err as Error);
        throw err;
      }
    },
    [user, recruitsHook]
  );

  // Update recruit
  const updateRecruit = useCallback(
    async (
      id: string,
      updates: Partial<RecruitmentFormValues>
    ): Promise<RecruitmentCandidate | null> => {
      if (!user?.id) {
return null;
}

      try {
        const dbUpdate = recruitUpdateToDatabase(updates, user.id);
        const dbRecruit = await updateRecruitDb(id, dbUpdate, user.id);
        const frontendRecruit = recruitFromDatabase(dbRecruit);

        // Refresh list
        await recruitsHook.refetch();

        return frontendRecruit;
      } catch (err) {
        log.error('Error updating recruit', { recruitId: id }, err as Error);
        throw err;
      }
    },
    [user?.id, recruitsHook]
  );

  // Delete recruit
  const deleteRecruit = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        await deleteRecruitDb(id);

        // Refresh list
        await recruitsHook.refetch();

        return true;
      } catch (err) {
        log.error('Error deleting recruit', { recruitId: id }, err as Error);
        return false;
      }
    },
    [recruitsHook]
  );

  return {
    // Recruits
    recruits: recruitsHook.recruits,
    recruitsLoading: recruitsHook.loading,
    recruitsError: recruitsHook.error,
    createRecruit,
    updateRecruit,
    deleteRecruit,
    refetchRecruits: recruitsHook.refetch,

    // Template
    template: templateHook.template,
    templateLoading: templateHook.loading,
    templateError: templateHook.error,
    saveTemplate: templateHook.saveTemplate,
    refetchTemplate: templateHook.refetch,

    // Instance (for specific recruit)
    instance: instanceHook.instance,
    instanceLoading: instanceHook.loading,
    instanceError: instanceHook.error,
    saveInstance: instanceHook.saveInstance,
    updateCheckboxStates: instanceHook.updateCheckboxStates,
    refetchInstance: instanceHook.refetch,

    // Combined states (backward compatibility with old hook)
    loading: recruitsHook.loading || templateHook.loading || instanceHook.loading,
    error: recruitsHook.error || templateHook.error || instanceHook.error
  };
}

// All hooks are exported above with 'export function' declarations
